import Enumerable from "linq";
import { ImageSourceKinds } from "../../domain/document";
import { Document } from "../../domain/document/Document";
import {
  IItem,
  instanceOfICheckable,
  instanceOfIValueContainable,
  instanceOfIImageDrawable,
} from "../../domain/document/items";
import { ISize, IThickness, ScreenDirection, PaintStyle } from "../../domain/drawing";
import {
  IWizard,
  IWizardPage,
  IWizardContent,
  WizardContentTypes,
  IWizardContentsContainable,
  IWizardHeadingIndicatable,
  IWizardFixedTextIndicatable,
  IWizardFormatReferable,
  IWizardFormatIdentifiable,
  IWizardObjectReferable,
  IWizardObjectIdentifiable,
  IWizardSizeSpecifiable,
  IWizardObjectSelectable,
  IWizardObjectOption,
  IWizardInputDecoratable,
  IWizardBarDrawable,
  IWizardRegistration,
  IWizardObjectDecoratable,
  IWizardScript,
  IWizardObjectAlternativeOption,
  IWizardObjectProcessorContainable,
  IWizardAttributes,
  IWholeWizardAttributes,
} from "../../domain/format";
import { IValueRange } from "../../domain/system";

export class Wizard implements IWizard {
  constructor(public readonly attributes: IWholeWizardAttributes, public readonly pages: IWizardPage[], public readonly registration: IWizardRegistration, public readonly registeredPages: IWizardPage[], public readonly scripts: IWizardScript[]) {}

  public isRegisterable(source: Document): boolean {
    return Enumerable.from(this.pages).all(p => p.isRegisterable(source));
  }
}

export interface IWizardValidatable {
  isValid(source: Document): boolean;
}

export interface IWizardPageOption {
  enableValidator?: IWizardValidatable;
  registerableValidator?: IWizardValidatable;
}

export class WizardPage implements IWizardPage {
  constructor(public readonly attributes: IWizardAttributes, public readonly contents: IWizardContent[], private readonly _option: IWizardPageOption = null) {}

  public isEnable(source: Document): boolean {
    return (!this._option || !this._option.enableValidator || this._option.enableValidator.isValid(source));
  }

  public isRegisterable(source: Document): boolean {
    return (!this._option || !this._option.registerableValidator || this._option.registerableValidator.isValid(source))
        && Enumerable.from(this.contents).all(c => c.isRegisterable(source));
  }
}

export interface IWizardContentOption {
  margin?: IThickness<string>;
  isMandatory?: boolean;
  visibleValidator?: IWizardValidatable;
  enableValidator?: IWizardValidatable;
  registerableValidator?: IWizardValidatable;
}

export class WizardContent<TOption extends IWizardContentOption = IWizardContentOption> implements IWizardContent {
  constructor(public readonly type: WizardContentTypes, public readonly privateId: string, public readonly attributes: IWizardAttributes, private readonly _option: TOption = null) {}

  public get margin(): IThickness<string> {
    return (this._option && this._option.margin)
      ? this._option.margin
      : {
        top: "0",
        left: "0",
        bottom: "0",
        right: "0",
      };
  }

  public isMandatory(source: Document): boolean {
    return this.isEnable(source) && this._option && this._option.isMandatory;
  }

  public isVisible(source: Document): boolean {
    return (!this._option || !this._option.visibleValidator || this._option.visibleValidator.isValid(source));
  }

  public isEnable(source: Document): boolean {
    return this.isVisible(source) && (!this._option || !this._option.enableValidator || this._option.enableValidator.isValid(source));
  }

  public isRegisterable(source: Document): boolean {
    return !this._option
        || ((!this.isMandatory(source) || this.isOwnInputted(source))
         && (!this._option.registerableValidator || this._option.registerableValidator.isValid(source)));
  }

  protected isOwnInputted(source: Document): boolean {
    // 入力コンテンツの場合overrideする事.
    return true;
  }
}

export class AbstractWizardContentContainer extends WizardContent implements IWizardContentsContainable {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, public readonly contents: IWizardContent[], option: IWizardContentOption = null, public readonly gap?: string, public readonly contentMargin?: string) {
    super(type, privateId, attributes, option);
  }

  public isRegisterable(source: Document): boolean {
    return !this.isEnable(source)
        || (super.isRegisterable(source) && Enumerable.from(this.contents).all(c => c.isRegisterable(source)));
  }
}

export class WizardContentContainer extends AbstractWizardContentContainer {
  constructor(privateId: string, attributes: IWizardAttributes, contents: IWizardContent[], option: IWizardContentOption = null, gap?: string, contentMargin?: string) {
    super("container", privateId, attributes, contents, option, gap, contentMargin);
  }
}

export class WizardContentHorizontalContainer extends AbstractWizardContentContainer {
  constructor(privateId: string, attributes: IWizardAttributes, contents: IWizardContent[], option: IWizardContentOption = null, gap?: string, contentMargin?: string) {
    super("horizontal-container", privateId, attributes, contents, option, gap, contentMargin);
  }
}

export class WizardContentStackContainer extends AbstractWizardContentContainer implements IWizardSizeSpecifiable {
  // StackContainerはgap、contentMarginには対応しない.(contentを同一位置に重ねるコンテナなので.)
  constructor(privateId: string, attributes: IWizardAttributes, contents: IWizardContent[], public readonly size: ISize<string>, option: IWizardContentOption = null) {
    super("stack-container", privateId, attributes, contents, option);
  }
}

export class WizardContentFixedTextIndicator extends WizardContent implements IWizardFixedTextIndicatable {
  constructor(privateId: string, attributes: IWizardAttributes, public readonly fixedText: string, option: IWizardContentOption = null) {
    super("fixed-text-indicator", privateId, attributes, option);
  }
}

export class WizardContentHeadingIndicator extends WizardContent implements IWizardHeadingIndicatable {
  constructor(privateId: string, attributes: IWizardAttributes, public readonly fixedText: string, option: IWizardContentOption = null) {
    super("heading-indicator", privateId, attributes, option);
  }
}

export class WizardContentPatientIndicator extends WizardContent {
  constructor(privateId: string, attributes: IWizardAttributes, option: IWizardContentOption = null) {
    super("patient-indicator", privateId, attributes, option);
  }
}

export class WizardContentFormatIndicator extends WizardContent {
  constructor(privateId: string, attributes: IWizardAttributes, option: IWizardContentOption = null) {
    super("format-indicator", privateId, attributes, option);
  }
}

export class WizardContentNotImplemented extends WizardContent {
  constructor(privateId: string, attributes: IWizardAttributes, type: WizardContentTypes, option: IWizardContentOption = null) {
    super(type, privateId, attributes, option);
  }
}

export class WizardContentMediaIndicator extends WizardContent implements IWizardSizeSpecifiable, IWizardObjectProcessorContainable {
  constructor(privateId: string, attributes: IWizardAttributes, public readonly objectIdentifier: IWizardObjectIdentifiable, public readonly size: ISize<string>, public readonly fixedText: string = null, option: IWizardContentOption = null, public processor: IWizardContent) {
    super("media-indicator", privateId, attributes, option);
  }
}

export class WizardContentInputtable extends WizardContent {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, private readonly _ownInputtedValidator: IWizardValidatable, option: IWizardContentOption = null) {
    super(type, privateId, attributes, option);
  }

  protected isOwnInputted(source: Document): boolean {
    return this._ownInputtedValidator.isValid(source);
  }
}

export class WizardContentObjectInputtable extends WizardContentInputtable implements IWizardObjectReferable {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, public readonly objectIdentifier: IWizardObjectIdentifiable, ownInputtedValidator: IWizardValidatable, option: IWizardContentOption = null) {
    super(type, privateId, attributes, ownInputtedValidator, option);
  }
}

export class WizardContentReferable extends WizardContent implements IWizardObjectReferable {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, public readonly objectIdentifier: IWizardObjectIdentifiable, option: IWizardContentOption = null) {
    super(type, privateId, attributes, option);
  }
}

export class WizardContentDecoratableObjectReferable extends WizardContentReferable implements IWizardObjectDecoratable {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super(type, privateId, attributes, objectIdentifier, option);
  }
}

export class WizardContentDecoratableObjectInputtable extends WizardContentObjectInputtable implements IWizardObjectDecoratable {
  constructor(type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, ownInputtedValidator: IWizardValidatable, public readonly decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super(type, privateId, attributes, objectIdentifier, ownInputtedValidator, option);
  }
}

export class WizardContentTextEditor extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, option: IWizardContentOption = null) {
    super("text-editor", privateId, attributes, objectIdentifier, new WizardTextInputtedValidator(objectIdentifier), option);
  }
}

export class WizardContentTextInput extends WizardContentDecoratableObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super("text-input", privateId, attributes, objectIdentifier, new WizardTextInputtedValidator(objectIdentifier), decoration, option);
  }
}

export class WizardContentNumberInput extends WizardContentDecoratableObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super("number-input", privateId, attributes, objectIdentifier, new WizardTextInputtedValidator(objectIdentifier), decoration, option);
  }
}

export class WizardContentBarInput extends WizardContentObjectInputtable implements IWizardBarDrawable, IWizardSizeSpecifiable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly size: ISize<string>, public readonly range: IValueRange, public readonly stepCount: number, public readonly direction: ScreenDirection, public readonly paintStyle: PaintStyle, option: IWizardContentOption = null) {
    super("bar-input", privateId, attributes, objectIdentifier, new WizardTextInputtedValidator(objectIdentifier), option);
  }
}

export class WizardContentDateInput extends WizardContentDecoratableObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super("date-input", privateId, attributes, objectIdentifier, new WizardDateInputtedValidator(objectIdentifier), decoration, option);
  }
}

export class WizardContentTextIndicator extends WizardContentDecoratableObjectReferable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, decoration: IWizardInputDecoratable, option: IWizardContentOption = null) {
    super("text-indicator", privateId, attributes, objectIdentifier, decoration, option);
  }
}

export class WizardContentOptionSelectable extends WizardContentInputtable implements IWizardObjectSelectable {
  constructor(public type: WizardContentTypes, privateId: string, attributes: IWizardAttributes, public readonly objectOptions: IWizardObjectOption[], option: IWizardContentOption = null) {
    super(type, privateId, attributes, WizardContentOptionSelectable.createOwnInputtedValidator(objectOptions), option);
  }

  private static createOwnInputtedValidator(options: IWizardObjectOption[]): IWizardValidatable {
    return new WizardValidationOrOperator(Enumerable.from(options).select(this.createObjectSelectedValidator).toArray());
  }

  private static createObjectSelectedValidator(option: IWizardObjectOption): IWizardValidatable {
    return option.content
      ? new WizardValidationAndOperator([
        new WizardObjectSelectedValidator(option.objectIdentifier),
        new WizardContentRegisterableValidator(option.content),
      ])
      : new WizardObjectSelectedValidator(option.objectIdentifier);
  }
}

export class WizardContentRadioSelector extends WizardContentOptionSelectable {
  constructor(privateId: string, attributes: IWizardAttributes, options: IWizardObjectOption[], option: IWizardContentOption = null, public readonly isHorizontal: boolean = false) {
    super("radio-selector", privateId, attributes, options, option);
  }
}

export class WizardContentRadioSelectors extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, public readonly objectIdentifier: IWizardObjectIdentifiable, option: IWizardContentOption = null, public readonly isHorizontal: boolean = false, public readonly visibleOptionIndexes: number[] = null, public readonly alternativeOptions: IWizardObjectAlternativeOption[] = null) {
    super("radio-selectors", privateId, attributes, objectIdentifier, new WizardOptionSelectedValidator(objectIdentifier, ""), option);
  }
}

export class WizardContentCheckSelector extends WizardContentOptionSelectable {
  constructor(privateId: string, attributes: IWizardAttributes, options: IWizardObjectOption[], option: IWizardContentOption = null, public readonly isHorizontal: boolean = false) {
    super("check-selector", privateId, attributes, options, option);
  }
}

export class WizardContentImageFileSelector extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("image-file-selector", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentImageCaptureInput extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("image-capture-input", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentImageSignInput extends WizardContentObjectInputtable implements IWizardSizeSpecifiable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly size: ISize<string>, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("image-sign-input", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentClaioImageSelector extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("claio-image-selector", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentClaioSchemaSelector extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("claio-schema-selector", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentMediaRemover extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, option: IWizardContentOption = null) {
    super("media-remover", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentMediaRotater extends WizardContentObjectInputtable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, public readonly fixedText: string = null, public readonly angle: number = null, option: IWizardContentOption = null) {
    super("media-rotater", privateId, attributes, objectIdentifier, new WizardMediaSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentUserSelector extends WizardContentObjectInputtable implements IWizardObjectReferable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, option: IWizardContentOption = null, public readonly isMultiSelectable: boolean = false) {
    super("user-selector", privateId, attributes, objectIdentifier, new WizardUserSelectedValidator(objectIdentifier), option);
  }
}

export class WizardContentLoginUserSelector extends WizardContentObjectInputtable implements IWizardObjectReferable {
  constructor(privateId: string, attributes: IWizardAttributes, objectIdentifier: IWizardObjectIdentifiable, option: IWizardContentOption = null) {
    super("login-user-selector", privateId, attributes, objectIdentifier, new WizardUserSelectedValidator(objectIdentifier), option);
  }
}

export class WizardFormatReference implements IWizardFormatReferable {
  constructor(public readonly formatIdentifier: IWizardFormatIdentifiable) {}
}

export class WizardFormatIdentifier implements IWizardFormatIdentifiable {
  constructor(public readonly formatId: number) {}
}

export class WizardObjectReference implements IWizardObjectReferable {
  constructor(public readonly objectIdentifier: IWizardObjectIdentifiable) {}
}

export class WizardObjectIdentifier implements IWizardObjectIdentifiable {
  constructor(public readonly id: string) {}
}

export class WizardInputDecoration implements IWizardInputDecoratable {
  constructor(public readonly prefix?: string, public readonly postfix?: string) {}
}

export interface IWizardObjectOptionOption {
  text?: string;
  content?: IWizardContent;
}

export class WizardObjectOption extends WizardObjectReference implements IWizardObjectOption {
  constructor(objectIdentifier: IWizardObjectIdentifiable, private readonly _option: IWizardObjectOptionOption = null) {
    super(objectIdentifier);
  }

  get text(): string {
    return (this._option && this._option.text)
      ? this._option.text
      : null;
  }

  get content(): IWizardContent {
    return (this._option && this._option.content)
      ? this._option.content
      : null;
  }
}

export class WizardValidationAndOperator implements IWizardValidatable {
  constructor(private readonly _children: IWizardValidatable[]) {}

  public isValid(source: Document): boolean {
    return Enumerable.from(this._children).all(c => c.isValid(source));
  }
}

export class WizardValidationOrOperator implements IWizardValidatable {
  constructor(private readonly _children: IWizardValidatable[]) {}

  public isValid(source: Document): boolean {
    return Enumerable.from(this._children).any(c => c.isValid(source));
  }
}

export class WizardValidationNotOperator implements IWizardValidatable {
  constructor(private readonly _child: IWizardValidatable) {}

  public isValid(source: Document): boolean {
    return !this._child.isValid(source);
  }
}

export class WizardContentRegisterableValidator implements IWizardValidatable {
  constructor(private readonly _content: IWizardContent) {}

  public isValid(source: Document): boolean {
    return this._content.isRegisterable(source);
  }
}

export abstract class AbstractWizardFormatValidator implements IWizardValidatable {
  constructor(private readonly _identifier: IWizardFormatIdentifiable) {}

  public isValid(source: Document): boolean {
    return Enumerable.from(this.toFormatIds(source))
      .any(i => i && (i === this._identifier.formatId) && this.isFormatValid(i));
  }

  private toFormatIds(source: Document): number[] {
    return [ source.attributes.format.originalId ].concat(this.toChainedFormatIds(source));
  }

  private toChainedFormatIds(source: Document): number[] {
    return (source.chainedFormatOriginalIds && (0 < source.chainedFormatOriginalIds.length))
      ? source.chainedFormatOriginalIds
      : source.chainableFormatOriginalIds;
  }

  public abstract isFormatValid(source: number): boolean;
}

export class WizardReferredFromFormatValidator extends AbstractWizardFormatValidator {
  constructor(identifier: IWizardFormatIdentifiable) {
    super(identifier);
  }

  public isFormatValid(source: number): boolean {
    return true;
  }
}

export abstract class AbstractWizardObjectValidator implements IWizardValidatable {
  constructor(private readonly _identifier: IWizardObjectIdentifiable) {}

  public isValid(source: Document): boolean {
    return Enumerable.from(source.itemSets[0].items)
      .any(i => i && i.id && (i.id === this._identifier.id) && this.isObjectValid(i));
  }

  public abstract isObjectValid(source: IItem): boolean;
}

export class WizardObjectSelectedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable) {
    super(identifier);
  }

  public isObjectValid(source: IItem): boolean {
    return instanceOfICheckable(source)
        && source.isChecked;
  }
}

export class WizardTextInputtedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable) {
    super(identifier);
  }

  public isObjectValid(source: IItem): boolean {
    return instanceOfIValueContainable(source)
        && source.value;
  }
}

export class WizardDateInputtedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable) {
    super(identifier);
  }

  public isObjectValid(source: IItem): boolean {
    return instanceOfIValueContainable(source)
        && source.value
        && source.value.value
        && (typeof(source.value.value) !== "string");
  }
}

export class WizardMediaSelectedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable) {
    super(identifier);
  }

  public isObjectValid(source: IItem): boolean {
    return instanceOfIImageDrawable(source)
      && !!source.imageSource.imageGuid
      && source.imageSource.imageSourceKind !== ImageSourceKinds.None;
  }
}

export class WizardUserSelectedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable) {
    super(identifier);
  }

  public isObjectValid(source: IItem): boolean {
    return instanceOfIValueContainable(source)
      && source.value;
  }
}

export class WizardOptionSelectedValidator extends AbstractWizardObjectValidator {
  constructor(identifier: IWizardObjectIdentifiable, optionSpecifiedText: string) {
    super(identifier);
    this.optionSpecifiedValue = optionSpecifiedText;
  }

  protected optionSpecifiedValue: string;

  public isObjectValid(source: IItem): boolean {
    return instanceOfIValueContainable(source)
      && source.value && this.isSelectedOptionValid(source.value);
  }

  public isSelectedOptionValid(selectedValue: string): boolean {
    // comboitem/rollitemはidやkeyが無く、外部処理からの値反映はtextで一致判定をしているため、同様にtextで判定をする
    return !this.optionSpecifiedValue || (selectedValue === this.optionSpecifiedValue);
  }
}
